export const extractUTMParams = (searchParams: { [key: string]: unknown }) => {
  const utmParams: { [key: string]: string } = {};

  const utmKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "utm_id",
    "fbclid", // Facebook click identifier
  ];

  utmKeys.forEach((key) => {
    const value = searchParams[key];
    // The router retypes what it can: `utm_content=42` arrives as a number,
    // `utm_medium=true` as a boolean. `String` restores what the URL carried
    // (exactly, see `lib/searchParams.ts`). `null` and `""` are left out on
    // purpose: `utm_term=null` and `utm_term=` are an unfilled campaign
    // macro, not a value — stored, the last touch would overwrite a real
    // first-touch value with nothing.
    if (
      (typeof value === "string" && value !== "") ||
      typeof value === "number" ||
      typeof value === "boolean"
    )
      utmParams[key] = String(value);
  });

  return utmParams;
};

const UTM_KEY = "utm_params";
const UTM_FIRST_TOUCH_KEY = "utm_params_first";

/**
 * Last touch in sessionStorage, first touch mirrored in localStorage and never
 * overwritten. The session copy only exists in the tab that landed on the ad
 * URL, so a purchase made anywhere else (return visit, new tab, in-app webview
 * left for Safari) would otherwise reach Stripe with no attribution at all.
 */
export const storeUTMParams = (utmParams: { [key: string]: string }) => {
  if (Object.keys(utmParams).length === 0) return;
  const serialized = JSON.stringify(utmParams);
  sessionStorage.setItem(UTM_KEY, serialized);
  if (!localStorage.getItem(UTM_FIRST_TOUCH_KEY))
    localStorage.setItem(UTM_FIRST_TOUCH_KEY, serialized);
};

/**
 * A truncated write used to die with the tab session; the first touch outlives
 * it, so an unguarded parse would break every checkout call on that browser for
 * good — and `clearUTMParams` has no call site to recover from it.
 */
const read = (storage: Storage, key: string): { [key: string]: string } => {
  const raw = storage.getItem(key);
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    storage.removeItem(key);
    return {};
  }
};

/**
 * Session scoped: "did this visit come from an ad". Tracking call sites read
 * this — they gate events and set Amplitude user properties, so answering
 * "has this browser ever come from an ad" would report every organic session
 * of an acquired user as paid.
 */
export const getUTMParams = (): { [key: string]: string } =>
  read(sessionStorage, UTM_KEY);

/**
 * First touch under last touch, merged per key. Checkout payloads read this:
 * they want the best attribution available, not the current visit's. Merging
 * rather than picking one keeps `utm_source` when a later URL carries only
 * `fbclid` (Meta strips `utm_*` on some placements) — at the cost of a payload
 * mixing the first campaign's source with the latest click id.
 */
export const getAttributionParams = (): { [key: string]: string } => ({
  ...read(localStorage, UTM_FIRST_TOUCH_KEY),
  ...read(sessionStorage, UTM_KEY),
});

export const clearUTMParams = () => {
  sessionStorage.removeItem(UTM_KEY);
  localStorage.removeItem(UTM_FIRST_TOUCH_KEY);
};
